import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { lastValueFrom, map, Subject, of, delay, catchError } from "rxjs";
import { environment } from "src/environments/environment";
import { OrderType } from "../interfaces/orders/order-type.interface";
import { Response } from "../interfaces/http/http.interface";
import { CreateOrder, Order } from "../interfaces/orders/order.interface";
import { DbService } from "./db.service";
import { AuthService } from "./auth.service";
import { AuditService } from "./audit.service";
import { Store } from "../interfaces/stores/store.interface";

// Add the MarketStall interface here for now:
export interface MarketStall {
  orderId?: number | null;
  referenceNumber?: string | null;
  orderDetailId?: number | null;
  orderDetailStoreId?: number | null;
  orderType?: string | null;
  status?: string | null;
  storeId: number;
  code: string;
  name: string;
  addressLine1: string;
  addressLine2?: string;
  country?: string;
  region: string;
  province?: string;
  city?: string;
  barangay?: string;
  zipCode?: string;
  area?: string;
  latitude?: string;
  longitude?: string;
  parentId?: number;
  images?: any[];
  installedPhotos?: any[];
  items?: any[] | null;
  parentStoreId?: number;
  parentStoreName?: string;
  visitStatus?: string;
  visitRemarks?: string;
  wave?: string | number; // Added wave property for filtering
}

@Injectable({
  providedIn: "root",
})
export class OrdersService {
  private http = inject(HttpClient);
  private db = inject(DbService);
  private authService = inject(AuthService);
  private auditService = inject(AuditService);

  // Subject to notify when a store's status changes
  storeStatusChanged$ = new Subject<Store>();

  constructor() {}

  startSync = () => {
    this.db.startSync();
  };

  stopSync = () => {
    this.db.stopSync();
  };

  fetchOrders() {
    return this.http.get<Response>(environment.APP_URL + "/orders").pipe(
      map((res: Response) => {
        console.log(res.data);

        return res.data as Order[];
      }),
    );
  }

  fetchOrdersByType = (type: string) => {
    return this.http.get<Response>(environment.APP_URL + "/orders").pipe(
      map((res: Response) => {
        const filteredList = (res.data as Order[]).filter(
          (order) => order.orderTypeName === type,
        );

        return filteredList as Order[];
      }),
    );
  };

  createOrder(data: CreateOrder) {
    return this.http.post<Response>(environment.APP_URL + "/orders", data).pipe(
      map((res: Response) => {
        return res.data as CreateOrder;
      }),
    );
  }

  deleteOrder = (data: Order) => {
    return this.http
      .delete<Response>(environment.APP_URL + "/orders", {
        body: { id: data.id },
      })
      .pipe(
        map((res: Response) => {
          return res.data as Order;
        }),
      );
  };

  fetchOrderTypes() {
    return this.http.get<Response>(environment.APP_URL + "/order-types").pipe(
      map((res: Response) => {
        return res.data as OrderType[];
      }),
    );
  }

  createOrderType(orderType: OrderType) {
    return this.http
      .post<Response>(environment.APP_URL + "/order-types", orderType)
      .pipe(
        map((res: Response) => {
          return res.data as OrderType;
        }),
      );
  }

  editOrderType(orderType: OrderType) {
    return this.http
      .put<Response>(environment.APP_URL + "/order-types", orderType)
      .pipe(
        map((res: Response) => {
          return res.data as OrderType;
        }),
      );
  }

  deleteOrderType = (orderType: OrderType) => {
    return this.http
      .delete<Response>(environment.APP_URL + "/order-types", {
        body: { id: orderType.id },
      })
      .pipe(
        map((res: Response) => {
          return res.data as OrderType;
        }),
      );
  };

  fetchStoreByReferenceId = (
    referenceNumber: string,
    action: string,
    storeId: string,
  ) => {
    console.log(action);

    if (
      action === "audit" ||
      action === "tasa" ||
      action === "delivery" ||
      action === "installation"
    ) {
      return this.auditService.getStoreAudit(referenceNumber, storeId);
    } else {
      return;
    }
  };

  fetchOrderByRef = async (referenceNumber: string) => {
    const currentUser = await this.authService.getCurrentUser();

    const orderDoc = await this.db.get(
      `orders/${currentUser}/${referenceNumber}`,
    );

    if (!(orderDoc as any).error) {
      return orderDoc as any;
    } else {
      return lastValueFrom(
        this.http
          .get<Response>(`${environment.APP_URL}/orders/${referenceNumber}`)
          .pipe(
            map(async (res: Response) => {
              await this.db.upsert(
                `orders/${currentUser}/${res.data.details.referenceNumber}`,
                res.data,
              );
              return res.data as Order[];
            }),
          ),
      );
    }
  };

  importOrders = (data: any) => {
    return this.http
      .post<Response>(environment.APP_URL + "/orders/import", data)
      .pipe(
        map((res: Response) => {
          return res.data;
        }),
      );
  };

  submitOrder = (store: any) => {
    return this.http
      .post<Response>(`${environment.APP_URL}/orders/submit-reports`, store)
      .pipe(
        map(async () => {
          await this.updateOrderStoresList(store);
          // Notify subscribers about the status change
          this.storeStatusChanged$.next(store);
        }),
      );
  };

  updateOrderStoresList = async (store: any) => {
    const currentUser = await this.authService.getCurrentUser();

    const storesDoc = await this.db.get(
      `orders/${currentUser}/${store.referenceNumber}`,
    );

    const storeIdx = (storesDoc as any).stores.findIndex(
      (storeItem: any) => storeItem.storeId === store.storeId,
    );

    (storesDoc as any).stores[storeIdx] = store;

    return await this.db.upsert(
      `orders/${currentUser}/${store.referenceNumber}`,
      storesDoc,
    );
  };

  getCurrentStoreReportToLocalDB = async (
    referenceNumber: string,
    storeId: string,
  ) => {
    const currentUser = await this.authService.getCurrentUser();

    return await this.db.get(
      `stores/${currentUser}/${referenceNumber}/${storeId}`,
      true,
    );
  };

  getCurrentStoreReportDraftFromLocalDB = async (
    referenceNumber: string,
    storeId: string,
  ) => {
    const currentUser = await this.authService.getCurrentUser();

    return await this.db.get(
      `drafts/${currentUser}/${referenceNumber}/${storeId}`,
      true,
    );
  };

  saveCurrentStoreReportToLocalDB = async (store: any) => {
    const currentUser = await this.authService.getCurrentUser();

    const list = await this.updateOrderStoresList(store);

    return await this.db.upsert(
      `stores/${currentUser}/${store.referenceNumber}/${store.storeId}`,
      store,
    );
  };

  saveCurrentStoreReportDraftFromLocalDB = async (store: any) => {
    const currentUser = await this.authService.getCurrentUser();

    return await this.db.upsert(
      `drafts/${currentUser}/${store.referenceNumber}/${store.id || store.storeId}`,
      store,
    );
  };

  // Save a draft store to local storage
  saveDraftStore = async (store: any) => {
    const currentUser = await this.authService.getCurrentUser();

    // Make sure the store is marked as a draft
    store.isDraft = true;

    return await this.db.upsert(
      `drafts/${currentUser}/${store.referenceNumber}/${store.id || store.storeId}`,
      store,
    );
  };

  // Save the entire drafts list for an order
  saveDraftStores = async (referenceNumber: string, draftStores: any[]) => {
    const currentUser = await this.authService.getCurrentUser();

    return await this.db.upsert(
      `drafts-list/${currentUser}/${referenceNumber}`,
      { stores: draftStores, updatedAt: new Date().toISOString() },
    );
  };

  // Get all draft stores for an order
  getDraftStores = async (referenceNumber: string) => {
    const currentUser = await this.authService.getCurrentUser();

    const drafts = await this.db.get(
      `drafts-list/${currentUser}/${referenceNumber}`,
      true,
    );

    if ((drafts as any).error) {
      return [];
    }

    return (drafts as any).stores || [];
  };

  // Get a specific draft store
  getDraftStore = async (referenceNumber: string, storeId: string) => {
    const currentUser = await this.authService.getCurrentUser();

    return await this.db.get(
      `drafts/${currentUser}/${referenceNumber}/${storeId}`,
      true,
    );
  };

  // Delete a draft store
  deleteDraftStore = async (referenceNumber: string, storeId: string) => {
    const currentUser = await this.authService.getCurrentUser();

    return await this.db.remove(
      `drafts/${currentUser}/${referenceNumber}/${storeId}`,
    );
  };

  fetchPublicMarkets() {
    return this.http
      .get<Response>(environment.APP_URL + "/orders/public-markets")
      .pipe(
        map((res: Response) => {
          return res.data as Order[];
        }),
      );
  }

  fetchMarketStalls() {
    return this.http.get<Response>(environment.APP_URL + '/orders/market-stalls').pipe(
      map((res: Response) => res.data as MarketStall[])
    );
  }

  // Import market stalls execution data
  importMarketStalls(marketStalls: any[]) {
    return this.http.post<Response>(environment.APP_URL + '/orders/market-stalls/import', {
      marketStalls
    }).pipe(
      map((res: Response) => res.data)
    );
  }

  // Update market stall installation data
  updateMarketStallInstallation(installationData: any) {
    return this.http.post<Response>(
      `${environment.APP_URL}/orders/market-stalls`, 
      installationData
    ).pipe(
      map((res: Response) => res.data),
      catchError((error) => {
        console.error('Error updating market stall installation:', error);
        throw error;
      })
    );
  }

  // Save public market remarks
  savePublicMarketRemarks(publicMarketId: string, remarks: string) {
    const payload = {
      publicMarketId: publicMarketId,
      remarks
    };
    
    return this.http
      .post<Response>(`${environment.APP_URL}/orders/public-markets/remarks`, payload)
      .pipe(
        map((res: Response) => {
          return res;
        }),
      );
  }

  // Save public market replacement
  savePublicMarketReplacement(data: {
    newPublicMarketId: number;
    currentPublicMarketId: number;
    orderDetailId: number;
    expectedBudget: number;
  }) {
    return this.http
      .post<Response>(`${environment.APP_URL}/orders/public-markets/replacement`, data)
      .pipe(
        map((res: Response) => {
          return res;
        }),
      );
  }
}
